import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from 'styled-components';

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 20px;
`;

const StyledButton = styled(Button)`
    background-color: ${(props) => props.styling?.widgetButtonBackgroundColor || 'grey'};
    color: ${(props) => props.styling?.textColor || 'white'};
    &:hover {
        background-color: ${(props) => props.styling?.hoverButtonBackgroundColor || 'darkgrey'};
    }
`;

const ActiveButton = styled(StyledButton)`
    background-color: ${(props) => props.styling?.navbarBackgroundHighlightColor || 'tomato'} !important;
    color: ${(props) => props.styling?.activeButtonTextColor || 'white'} !important;
`;

const Pagination = ({ currentPage, totalPages, onChangePage, styling }) => {
    const visiblePages = 3;
    const isMobile = useMediaQuery('(max-width: 700px)');

    const getPageNumbers = () => {
        let startPage = 1;
        let endPage = totalPages;

        if (totalPages > visiblePages) {
            const halfVisiblePages = Math.floor(visiblePages / 2);
            startPage = Math.max(Math.min(currentPage - halfVisiblePages, totalPages - visiblePages + 1), 1);
            endPage = Math.min(startPage + visiblePages - 1, totalPages);
        }

        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    };

    return (
        <PaginationWrapper>
            {isMobile ? (
                <>
                    <StyledButton
                        styling={styling}
                        onClick={() => onChangePage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        ←
                    </StyledButton>
                    <StyledButton
                        styling={styling}
                        onClick={() => onChangePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        →
                    </StyledButton>
                </>
            ) : (
                <>
                    <StyledButton
                        styling={styling}
                        onClick={() => onChangePage(1)}
                        disabled={currentPage === 1}
                    >
                        &laquo; 1
                    </StyledButton>

                    {getPageNumbers().map((pageNumber) =>
                        currentPage === pageNumber ? (
                            <ActiveButton
                                key={pageNumber}
                                styling={styling}
                                onClick={() => onChangePage(pageNumber)}
                            >
                                {pageNumber}
                            </ActiveButton>
                        ) : (
                            <StyledButton
                                key={pageNumber}
                                styling={styling}
                                onClick={() => onChangePage(pageNumber)}
                            >
                                {pageNumber}
                            </StyledButton>
                        )
                    )}

                    <StyledButton
                        styling={styling}
                        onClick={() => onChangePage(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        {totalPages} &raquo;
                    </StyledButton>
                </>
            )}
        </PaginationWrapper>
    );
};

Pagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onChangePage: PropTypes.func.isRequired,
    styling: PropTypes.shape({
        widgetButtonBackgroundColor: PropTypes.string,
        textColor: PropTypes.string,
        hoverButtonBackgroundColor: PropTypes.string,
        navbarBackgroundHighlightColor: PropTypes.string,
        activeButtonTextColor: PropTypes.string,
    }),
};

export default Pagination;
